import * as React from 'react'
import { DayPicker, getDefaultClassNames } from 'react-day-picker'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import OnboardingModal from '@builder/react-ui/scheduler/onboarding-modal'
import { IoCalendarOutline as EditDateIcon } from 'react-icons/io5'
import * as Ariakit from '@ariakit/react'
import { SaveScheduleContext, OpenCalendarDialog } from '@react-ui/context'
import SessionCardMobileButton from '@builder/react-ui/scheduler/session-card-mobile/session-card-mobile-button'

export {
  PopoverArrow,
  PopoverDescription,
  PopoverDismiss,
  PopoverHeading
} from '@ariakit/react'
interface SessionCalendarProps {
  id: string
  category: string
  sessionDate: Date
  isDisabled: boolean
  isTraining: boolean
  isMobile?: boolean
}

const SessionCalendar: FunctionComponent<SessionCalendarProps> = ({
  category,
  id,
  sessionDate,
  isDisabled = true,
  isTraining,
  isMobile = false
}) => {
  const [selected, setSelected] = useState<Date>(sessionDate)
  const [showDialog, setShowDialog] = useState(false)
  const [showPopover, setShowPopover] = useState(false)
  const [month, setMonth] = useState(sessionDate)
  const { saveSessionDate, sessions } = useContext(SaveScheduleContext)
  const menu = Ariakit.useMenuStore()

  const {
    isDragged,
    activeSessionId,
    resetDragging,
    sessionDateBefore,
    sessionDateAfter
  } = useContext(OpenCalendarDialog)

  useEffect(() => {
    if (isDragged === true && activeSessionId === id) {
      setShowDialog(true)
      resetDragging()
    }
  }, [isDragged, activeSessionId, id])

  useEffect(() => {
    if (!sessionDate) {
      sessionDate = new Date()
    }

    setSelected(sessionDate)
  }, [])
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Prevent closing the calendar
      event.preventDefault()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])
  const defaultClassNames = getDefaultClassNames()
  const title = 'Confirm or Choose a New Date'

  const highlightedDates = isTraining
    ? sessions.training
    : sessions.course
        .filter((item) => {
          return item.status !== 'completed' && item.skipped !== true
        })
        .map(({ date }) => date)

  function openPopover() {
    setShowPopover(true)
    setMonth(selected)
  }

  function closePopover() {
    setShowPopover(false)
  }

  function closeDialog() {
    setShowDialog(false)
  }

  function onDateSelect(date: Date | undefined) {
    if (date) {
      setSelected(date)
      saveSessionDate(id, date, category)
    } else {
      saveSessionDate(id, selected, category)
    }
  }

  function onSelectDialog(date: Date | undefined) {
    onDateSelect(date)
    closeDialog()
  }

  function onSelectPopover(date: Date | undefined) {
    onDateSelect(date)
    closePopover()
    menu.hide()
  }

  const DayPickerComponent = ({ clickFunction, testId, isDialog }) => {
    return isDialog ? (
      <DayPicker
        mode='single'
        defaultMonth={
          sessionDateBefore
            ? (sessionDateBefore as Date)
            : (sessionDateAfter as Date)
        }
        selected={selected}
        onSelect={clickFunction}
        disabled={[
          { after: sessionDateAfter as Date },
          { before: sessionDateBefore as Date },
          { before: new Date() }
        ]}
        modifiers={{
          highlighted: highlightedDates
        }}
        modifiersStyles={{
          highlighted: {
            backgroundColor: '#fecaca', // Light red
            borderRadius: '2rem'
          },
          selected: {
            backgroundColor: '#dc2626', // Red
            color: '#fff',
            borderRadius: '2rem'
          }
        }}
        classNames={{
          today: `border-amber-500`, // Add a border to today's date
          selected: `bg-amber-500 border-amber-500 text-white`, // Highlight the selected day
          root: `${defaultClassNames.root} shadow-lg p-5 rounded-md relative w-fit`, // Add a shadow to the root element
          day: `  relative text-[#3D3937] font-light`,
          chevron: `#242424`, // Change the color of the chevron
          months: 'flex flex-col',
          month_caption: 'flex justify-center pt-1 relative items-center',
          nav: 'space-x-1'
          // button_previous: `absolute left-6  bg-white border-0 cursor-pointer z-10`,
          // button_next: `absolute right-6  bg-white border-0 cursor-pointer z-10`
        }}
        className='p-4 bg-white rounded-lg shadow'
        data-qa-name={testId}
      />
    ) : (
      <DayPicker
        mode='single'
        month={month}
        onMonthChange={setMonth}
        selected={selected}
        onSelect={clickFunction}
        disabled={[{ before: new Date() }]}
        modifiers={{
          highlighted: highlightedDates
        }}
        modifiersStyles={{
          highlighted: {
            backgroundColor: '#fecaca', // Light red
            borderRadius: '2rem'
          },
          selected: {
            backgroundColor: '#dc2626', // Red
            color: '#fff',
            borderRadius: '2rem'
          }
        }}
        classNames={{
          today: `border-amber-500`, // Add a border to today's date
          selected: `bg-amber-500 border-amber-500 text-white`, // Highlight the selected day
          root: `${defaultClassNames.root} shadow-lg p-5 rounded-md relative w-fit`, // Add a shadow to the root element
          day: `  relative text-[#3D3937] font-light`,
          chevron: `#242424`, // Change the color of the chevron
          months: 'flex flex-col',
          month_caption: 'flex justify-center pt-1 relative items-center',
          nav: 'space-x-1'
          // button_previous: `absolute left-6  bg-white border-0 cursor-pointer z-10`,
          // button_next: `absolute right-6  bg-white border-0 cursor-pointer z-10`
        }}
        className='p-4 bg-white rounded-lg shadow'
        data-qa-name={testId}
      />
    )
  }

  return (
    <>
      <>
        {isMobile ? (
          <Ariakit.PopoverDisclosure
            className='border-none'
            disabled={isDisabled}
            store={menu}
            data-qa-name='popover-disclosure'
            onClick={openPopover}
            render={<div className={'flex justify-stretch'}></div>}
          >
            <SessionCardMobileButton
              icon={<EditDateIcon />}
              disabled={isDisabled}
              text={$localize`:schedule.button|Schedule - Edit Date Button@@scheduleEditDateButton:Edit Date`}
            />
          </Ariakit.PopoverDisclosure>
        ) : (
          <Ariakit.PopoverDisclosure
            className='group group-aria-expanded:group-hover:opacity-0 h-full border-none aria-expanded:bg-alpha-red bg-black rounded-full px-3 py-2.5  disabled:bg-alpha-oatmeal-20'
            disabled={isDisabled}
            store={menu}
            data-qa-name='popover-disclosure'
            onClick={openPopover}
          >
            <div className=' relative flex justify-center items-center gap-[4px] bg-transparent text-white  w-fit'>
              <EditDateIcon
                size={24}
                data-qa-name='calendar-icon'
                className='text-2xl cursor-pointer'
              />

              <span className='absolute text-white text-sm font-medium rounded-full whitespace-nowrap transition-all duration-[50ms] bg-transparent opacity-0 group-hover:opacity-100 group-hover:relative group-hover:left-0.25 group-aria-expanded:hidden '>
                Edit
              </span>
            </div>
          </Ariakit.PopoverDisclosure>
        )}

        <Ariakit.Popover store={menu} className='z-50'>
          {showPopover && (
            <DayPickerComponent
              isDialog={false}
              testId='popover'
              clickFunction={onSelectPopover}
            />
          )}
        </Ariakit.Popover>
      </>

      {showDialog && (
        <OnboardingModal openDialog={true} showClose={false} heading={title}>
          <DayPickerComponent
            isDialog={true}
            testId='dialog'
            clickFunction={onSelectDialog}
          />
        </OnboardingModal>
      )}
    </>
  )
}

export default SessionCalendar
